import React from 'react';

const formatCurrency = (value) => {
  if (value == null || isNaN(value)) {
    return '$0';
  }
  return `$${Number(value).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const TraditionalRentalExecSummary = ({ investorResult, borrowerResult, yearlyData, params }) => {
  if (!investorResult || !yearlyData || !params) {
    return <div className="text-center text-gray-500">No data available.</div>;
  }

  return (
    <div className="bg-gray-50 p-6 rounded-lg text-sm text-gray-800 leading-relaxed space-y-4">
      <h3 className="text-lg font-bold text-gray-900">Executive Summary: Traditional Rental Model</h3>

      <p>
        This <strong>Traditional Rental</strong> model simulates a long-term landlord investment over a {investorResult.termYears}-year period,
        where the investor purchases a property valued at <strong>{formatCurrency(params.propertyValue)}</strong> and collects rent from a tenant 
        without offering equity or ownership. The model assumes annual property appreciation of <strong>{params.propertyGrowthRate}%</strong> and 
        rental increase of <strong>{params.rentGrowthRate}%</strong>.
      </p>

      <h4 className="font-semibold text-green-800">📈 Investor Highlights</h4>
      <ul className="list-disc list-inside space-y-1">
        <li>Total Gross Rent Collected: <strong>{formatCurrency(investorResult.totalRentCollected)}</strong></li>
        <li>Net Cashflow (After All Expenses): <strong>{formatCurrency(investorResult.netCashflow)}</strong></li>
        <li>Admin Fee Paid (Cumulative): <strong className="text-red-600">−{formatCurrency(investorResult.totalAdminFees)}</strong></li>
        <li>Vacancy Loss Over Time: <strong className="text-red-600">−{formatCurrency(investorResult.totalVacancyLoss)}</strong></li>
        <li>Total Operating Expenses (Tax + Insurance + Maintenance + Admin): <strong>{formatCurrency(investorResult.cumulativeExpenses)}</strong></li>
        <li>Final Property Value: <strong>{formatCurrency(investorResult.finalValue)}</strong></li>
        <li>Sell Cost Deducted at Exit: <strong className="text-red-600">−{formatCurrency(investorResult.sellCost)}</strong></li>
        <li>Net Asset Value After Sale: <strong>{formatCurrency(investorResult.netAssetValueAfterSale)}</strong></li>
        <li>Total Exit Value (Cashflow + Net Sale Proceeds): <strong>{formatCurrency(investorResult.exitValue)}</strong></li>
        <li>Final Net Profit (Exit − Initial Investment): <strong className="text-green-700">{formatCurrency(investorResult.finalNetProfit)}</strong></li>
        <li>Total ROI: <strong>{investorResult.totalROI}%</strong></li>
        <li>Annualized ROI: <strong>{investorResult.annualizedROI}%</strong></li>
        <li>Cash-on-Cash Return: <strong>{investorResult.cashOnCashReturn}%</strong></li>
        <li>IRR Estimate: <strong>{investorResult.irrEstimate}%</strong></li>
        <li>Annual NOI: <strong>{formatCurrency(investorResult.annualNOI)}</strong></li>
        <li>Cap Rate: <strong>{investorResult.capRate}%</strong></li>
      </ul>

      <h4 className="font-semibold text-purple-800">🏠 Renter Perspective</h4>
      <ul className="list-disc list-inside space-y-1">
        <li>First Month Rent: <strong>{formatCurrency(investorResult.firstYearRent / 12)}</strong></li>
        <li>Last Month Rent: <strong>{formatCurrency(investorResult.lastYearRent / 12)}</strong></li>
        <li>Total Rent Paid Over Term: <strong>{formatCurrency(investorResult.totalBorrowerPaid)}</strong></li>
        <li>Equity Built: <strong>0%</strong> (No Ownership)</li>
      </ul>

      <p>
        This model reflects a classic landlord–tenant structure. The investor benefits from predictable rental income and long-term asset appreciation,
        while the renter receives no equity participation. It is ideal for scenarios focused purely on income generation and asset holding without equity-sharing dynamics.
      </p>
    </div>
  );
};

export default TraditionalRentalExecSummary;
